import Sheet from "components/sheet/Sheet";
import Portal from "elements/Portal";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ICourse } from "redux/reducers/models/facility";
import "./teeSheetSideBySideSettingsModal.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDistributeSpacingHorizontal, faDistributeSpacingVertical } from "@fortawesome/pro-regular-svg-icons";
import classNames from "classnames";
import Checkbox from "components/form/checkbox/Checkbox";
import { cloneDeep } from "lodash";
import {
  defaultSideBySideSettings,
  ITeeSheetSideBySideSettings,
} from "hooks/useTeeSheetSideBySideSettings/useTeeSheetSideBySideSettings";

interface ITeeSheetSideBySideModalProps {
  open: boolean;
  onCancel: () => void;
  onOk: (updatedSideBySideSettings: ITeeSheetSideBySideSettings) => void;
  currentSideBySideSettings: ITeeSheetSideBySideSettings;
  courses: ICourse[];
}

const MINIMUM_ALLOWED_SELECTED_DIVISIONS = 1;
const MAXIMUM_ALLOWED_SELECTED_DIVISIONS = 3;

export default function TeeSheetSideBySideSettingsModal(props: ITeeSheetSideBySideModalProps) {
  const { open, onCancel, onOk, currentSideBySideSettings, courses } = props;
  const { t } = useTranslation();

  const [sideBySideSettings, setSideBySideSettings] = useState<ITeeSheetSideBySideSettings>(null);

  useEffect(() => {
    if (open) {
      setSideBySideSettings(cloneDeep(currentSideBySideSettings));
    } else {
      setSideBySideSettings(null);
    }
  }, [open, currentSideBySideSettings]);

  function setSelectedDivisionId(selectedDivisionId: number) {
    if (sideBySideSettings.selectedDivisionIds.includes(selectedDivisionId)) {
      setSideBySideSettings(prev => ({
        ...prev,
        selectedDivisionIds: prev.selectedDivisionIds.filter(
          prevSelectedDivisionId => prevSelectedDivisionId !== selectedDivisionId,
        ),
      }));
    } else if (sideBySideSettings.selectedDivisionIds.length < MAXIMUM_ALLOWED_SELECTED_DIVISIONS) {
      setSideBySideSettings(prev => ({
        ...prev,
        selectedDivisionIds: [...prev.selectedDivisionIds, selectedDivisionId],
      }));
    }
  }

  return (
    <Portal isMounted={open}>
      <Sheet
        open={open}
        title={t("Select divisions")}
        supportText={t("Select up to {{maximumAllowedSelectedDivisions}} divisions to view on your tee sheet", {
          maximumAllowedSelectedDivisions: MAXIMUM_ALLOWED_SELECTED_DIVISIONS,
        })}
        size="small"
        closable
        onCancel={onCancel}
        onOk={() => onOk(sideBySideSettings)}
        okDisabled={
          sideBySideSettings == null ||
          (sideBySideSettings.selectedDivisionIds.length < MINIMUM_ALLOWED_SELECTED_DIVISIONS &&
            sideBySideSettings.enabled) ||
          sideBySideSettings.selectedDivisionIds.length > MAXIMUM_ALLOWED_SELECTED_DIVISIONS
        }
        okText={t("Confirm")}
        onSecondary={() => setSideBySideSettings(cloneDeep(defaultSideBySideSettings))}
        secondaryText={t("Clear")}
      >
        {sideBySideSettings && (
          <div className="tee-sheet-side-by-side-settings-content">
            <div className="side-by-side-settings-enable-container">
              <Checkbox
                label={t("Multiple divisions active")}
                size="small"
                checked={sideBySideSettings.enabled}
                value={sideBySideSettings.enabled}
                onChange={() => setSideBySideSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
              />

              <Checkbox
                label={t("Hide turn times")}
                size="small"
                checked={sideBySideSettings.hideTurnTimes}
                value={sideBySideSettings.hideTurnTimes}
                onChange={() => setSideBySideSettings(prev => ({ ...prev, hideTurnTimes: !prev.hideTurnTimes }))}
              />

              <Checkbox
                label={t("Hide blocked times")}
                size="small"
                checked={sideBySideSettings.hideBlockedTimes}
                value={sideBySideSettings.hideBlockedTimes}
                onChange={() => setSideBySideSettings(prev => ({ ...prev, hideBlockedTimes: !prev.hideBlockedTimes }))}
              />

              <Checkbox
                label={t("Sync division scrolling")}
                size="small"
                checked={sideBySideSettings.syncDivisionScroll}
                value={sideBySideSettings.syncDivisionScroll}
                onChange={() =>
                  setSideBySideSettings(prev => ({ ...prev, syncDivisionScroll: !prev.syncDivisionScroll }))
                }
              />
            </div>
            <div className="side-by-side-settings-views">
              <button
                className={classNames("side-by-side-settings-view", {
                  "side-by-side-settings-view-active": sideBySideSettings.view === "stacked",
                })}
                onClick={() => setSideBySideSettings(prev => ({ ...prev, view: "stacked" }))}
              >
                <FontAwesomeIcon className="side-by-side-settings-view-icon" icon={faDistributeSpacingVertical} />
                <div>{t('Stacked')}</div>
              </button>
              <button
                className={classNames("side-by-side-settings-view", {
                  "side-by-side-settings-view-active": sideBySideSettings.view === "side-by-side",
                })}
                onClick={() => setSideBySideSettings(prev => ({ ...prev, view: "side-by-side" }))}
              >
                <FontAwesomeIcon className="side-by-side-settings-view-icon" icon={faDistributeSpacingHorizontal} />
                <div>{t('Side by side')}</div>
              </button>
            </div>
            <div className="side-by-side-settings-divisions">
              {courses?.map(course => (
                <React.Fragment key={course.id}>
                  {course?.divisions?.map(division => {
                    const selectedDivision = sideBySideSettings.selectedDivisionIds.some(
                      selectedDivisionId => selectedDivisionId === division.id,
                    );

                    return (
                      <Checkbox
                        key={division.id}
                        size="small"
                        label={`${course.full_name}: ${division.title}`}
                        value={selectedDivision}
                        checked={selectedDivision}
                        disabled={
                          !selectedDivision &&
                          sideBySideSettings.selectedDivisionIds.length >= MAXIMUM_ALLOWED_SELECTED_DIVISIONS
                        }
                        onChange={() => setSelectedDivisionId(division.id)}
                      />
                    );
                  })}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}
      </Sheet>
    </Portal>
  );
}
